"use client";

import { useRef, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { MessageSquareText } from "lucide-react";

const VIDEO_URL = "https://depot.moondream.ai/seg/baby_penguins.mp4";

const SegmentSkillExplained = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container || hasPlayed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPlayed) {
            video.play().catch(() => {});
            setHasPlayed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.8 }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [hasPlayed]);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.loop = true;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.loop = false;
    }
  };

  return (
    <section className="pt-4 container max-w-6xl mx-auto px-4">
      <div>
        <div className="flex-row-reverse lg:flex">
          <div className="lg:w-1/2">
            <div className="mb-0">
              <div
                ref={containerRef}
                className="relative block overflow-hidden rounded-md leading-none"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  className="aspect-4/3 w-full object-contain bg-transparent"
                  poster="https://depot.moondream.ai/seg/baby_penguins-cover.jpeg"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
          <div className="lg:flex lg:w-1/2 lg:items-center lg:pr-24 2xl:pr-32">
            <div>
              <Badge
                variant="outline"
                className="mb-4 w-fit gap-1 bg-card px-3 text-sm font-normal tracking-tight shadow-sm"
              >
                <MessageSquareText className="size-4" />
                Open-vocabulary
              </Badge>
              <h3 className="mb-3 text-xl font-semibold md:mb-4 md:text-4xl lg:mb-6">
                Beyond fixed classes
              </h3>
              <p className="text-muted-foreground lg:text-lg">
                Traditional segmentation only recognizes what it was trained on. Need
                &ldquo;hairline cracks&rdquo; or &ldquo;overripe fruit&rdquo;? If it&apos;s
                not in the training set, you&apos;re stuck — curating data, labeling
                examples, retraining models.
              </p>
              <p className="text-muted-foreground lg:text-lg mt-4">
                Moondream understands language, not just labels. Describe any object, any
                attribute, any spatial relationship. Get pixel-accurate boundaries instantly.
                Your vocabulary is the only limit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { SegmentSkillExplained };
